import type { ReactNode } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Text, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';
import { api } from './api';
import { useAuth } from './auth';

const useStyles = makeStyles({
  page: { minHeight: '100vh', backgroundColor: tokens.colorNeutralBackground2 },
  bar: {
    display: 'flex', alignItems: 'center', ...shorthands.gap('24px'), ...shorthands.padding('0', '24px'), height: '52px',
    backgroundColor: '#4657D2', color: '#ffffff',
  },
  brand: { display: 'flex', alignItems: 'center', ...shorthands.gap('10px'), fontWeight: 600, fontSize: '16px', color: '#ffffff', textDecorationLine: 'none' },
  logo: { width: '24px', height: '24px' },
  nav: { display: 'flex', ...shorthands.gap('4px'), flexGrow: 1 },
  link: {
    ...shorthands.padding('6px', '12px'), ...shorthands.borderRadius('4px'), color: 'rgba(255,255,255,0.85)', textDecorationLine: 'none', fontSize: '14px',
    ':hover': { backgroundColor: 'rgba(255,255,255,0.14)' },
  },
  active: { backgroundColor: 'rgba(255,255,255,0.22)', color: '#ffffff', fontWeight: 600 },
  main: { maxWidth: '1180px', ...shorthands.margin('0', 'auto'), ...shorthands.padding('28px', '24px', '48px') },
  who: { cursor: 'pointer', display: 'flex', alignItems: 'center', ...shorthands.gap('8px'), color: '#ffffff' },
});

export function Shell({ children }: { children: ReactNode }) {
  const s = useStyles();
  const { user, isAdmin } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const health = useQuery({ queryKey: ['health'], queryFn: () => api<{ version: string }>('/health'), staleTime: 5 * 60_000 });
  const signOut = async () => {
    await api('/auth/logout', { method: 'POST', body: {} }).catch(() => undefined);
    await qc.invalidateQueries();
    qc.clear();
    navigate('/');
  };
  const link = ({ isActive }: { isActive: boolean }) => mergeClasses(s.link, isActive && s.active);

  return (
    <div className={s.page}>
      <header className={s.bar}>
        <NavLink to="/" className={s.brand}>
          <img src="/favicon.svg" alt="" className={s.logo} />
          Voxshift Hub
        </NavLink>
        <nav className={s.nav} aria-label="Main">
          <NavLink to="/" end className={link}>Instances</NavLink>
          <NavLink to="/knowledge" className={link}>Knowledge base</NavLink>
          {isAdmin && <NavLink to="/people" className={link}>People</NavLink>}
          {isAdmin && <NavLink to="/activity" className={link}>Activity</NavLink>}
          {isAdmin && <NavLink to="/settings" className={link}>Settings</NavLink>}
        </nav>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <div className={s.who} role="button" tabIndex={0} aria-label="Account menu">
              <Text style={{ color: '#ffffff' }}>{user?.displayName}</Text>
              <Avatar name={user?.displayName} size={28} color="colorful" />
            </div>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem onClick={() => navigate('/password')}>Change password</MenuItem>
              <MenuItem onClick={() => void signOut()}>Sign out</MenuItem>
              {health.data && <MenuItem disabled>Voxshift Hub {health.data.version}</MenuItem>}
            </MenuList>
          </MenuPopover>
        </Menu>
      </header>
      <main className={s.main}>{children}</main>
    </div>
  );
}
